import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { scrollToSection, EASE } from "@/lib/scroll";

const PERKS = [
  "Bikes to borrow",
  "High-speed Wi-Fi",
  "Washer & dryer",
  "Linen package",
  "BBQ & private deck",
  "Window AC",
];

export default function Rates() {
  return (
    <section
      id="rates"
      data-testid="rates-section"
      className="bg-[#1E3323] py-24 text-[#F7F4EF] sm:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-xl">
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-12 bg-[#C8634B]" />
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C8634B]">
                Rates & Seasons
              </p>
            </div>
            <h2 className="font-serif text-3xl leading-snug tracking-tight sm:text-4xl lg:text-5xl">
              2027 <span className="italic text-[#E8D8CF]">Pricing.</span>
            </h2>
          </div>
          <p className="max-w-xs font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-[#F7F4EF]/60">
            Mon 4pm check-in · Sun noon check-out · Now taking enquiries
          </p>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-5">
          <motion.div
            data-testid="rates-card-peak"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="relative rounded-2xl bg-[#F7F4EF] p-8 text-[#1A1C19] shadow-2xl shadow-black/30 transition-transform duration-300 hover:-translate-y-1.5 sm:p-10 lg:col-span-3"
          >
            <span className="absolute -top-3 left-8 rounded-full bg-[#C8634B] px-4 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F7F4EF]">
              The 2027 Rate
            </span>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#C8634B]">
              July & August · Two-week minimum
            </p>
            <h3 className="mt-3 font-serif text-2xl sm:text-3xl">
              The Summer Fortnight
            </h3>
            <p className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-5xl tracking-tight sm:text-6xl">
                $2,400
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#767970]">
                / two weeks
              </span>
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[#4A4D46]">
              Six sleep comfortably across the master bedroom, the bunk room,
              and the sleeping nook, with everything below included. Staying
              longer than two weeks? Discounts available, just ask.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {PERKS.map((perk) => (
                <span key={perk} className="flex items-center gap-2 text-sm text-[#4A4D46]">
                  <Check size={14} className="shrink-0 text-[#C8634B]" />
                  {perk}
                </span>
              ))}
            </div>
            <button
              data-testid="rates-card-peak-enquire-button"
              onClick={() => scrollToSection("#enquire")}
              className="mt-8 w-full rounded-full bg-[#1E3323] py-3.5 text-sm font-semibold tracking-wide text-[#F7F4EF] transition-colors duration-300 hover:bg-[#C8634B] sm:w-auto sm:px-10"
            >
              Enquire for your fortnight
            </button>
          </motion.div>

          <motion.div
            data-testid="rates-card-custom"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
            className="flex flex-col rounded-2xl border border-white/10 bg-white/5 p-8 transition-transform duration-300 hover:-translate-y-1.5 sm:p-10 lg:col-span-2"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#F7F4EF]/60">
              May 24 – Jun 27 · Sep 6 – Oct 10
            </p>
            <h3 className="mt-3 font-serif text-2xl sm:text-3xl">
              A different rhythm?
            </h3>
            <p className="mt-6 flex items-baseline gap-3">
              <span className="font-serif text-5xl tracking-tight sm:text-6xl">
                $1,000
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#F7F4EF]/60">
                / week
              </span>
            </p>
            <p className="mt-4 flex-1 text-sm leading-relaxed text-[#F7F4EF]/70">
              One-week stays are welcome from May 24 through June 27 and
              September 6 through October 10. Check in Monday at 4pm, out by
              Sunday noon. Tell us your dates and we'll figure it out
              together.
            </p>
            <button
              data-testid="rates-card-custom-enquire-button"
              onClick={() => scrollToSection("#enquire")}
              className="mt-8 w-full rounded-full border border-[#F7F4EF]/30 py-3.5 text-sm font-semibold tracking-wide text-[#F7F4EF] transition-all duration-300 hover:border-[#C8634B] hover:bg-[#C8634B]"
            >
              Ask about other dates
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
